import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, Plus, Copy, ExternalLink, Trash2, Users } from "lucide-react";
import { BrandLogo } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { adminClients, createClient, deleteClient } from "@/lib/api";
import { toast } from "sonner";

const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";
const field = "w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-brand";

export default function AdminClients() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState(null);
  const [form, setForm] = useState({ name: "", website: "", email: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user === null || (user && user.role !== "admin")) navigate("/login"); }, [user, navigate]);
  const load = () => adminClients().then((d) => setClients(d.clients)).catch(() => setClients([]));
  useEffect(() => { load(); }, []);

  if (user === undefined || user === null || user.role !== "admin")
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-brand" size={26} /></div>;

  const add = async () => {
    if (!form.name || !form.website) { toast.error("Name and website required"); return; }
    setBusy(true);
    try { await createClient(form); setForm({ name: "", website: "", email: "" }); await load(); toast.success("Client added"); }
    catch { toast.error("Could not add client"); } finally { setBusy(false); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this client dashboard?")) return; await deleteClient(id); load(); toast.success("Deleted"); };
  const linkFor = (t) => `${ORIGIN}/dashboard/${t}`;
  const copy = (t) => { navigator.clipboard.writeText(linkFor(t)); toast.success("Share link copied"); };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between sticky top-0 z-30">
        <Link to="/"><BrandLogo size={34} /></Link>
        <div className="flex items-center gap-4">
          <Link to="/admin" className="text-sm text-slate-500 hover:text-brand">Console</Link>
          <span className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Users size={15} /> Clients</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/admin" className="text-slate-500 hover:text-brand text-sm flex items-center gap-1.5 mb-2"><ArrowLeft size={14} /> Back to console</Link>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Client Dashboards</h1>
        <p className="text-slate-500 mt-1 text-sm mb-6">Give each client a shareable, no-login analytics page for their site.</p>

        {/* Add client */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6">
          <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5"><Plus size={15} className="text-brand" /> New client</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Client name" className={field} data-testid="client-name-input" />
            <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="client-site.com" className={field} data-testid="client-website-input" />
            <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="contact email (optional)" className={field} data-testid="client-email-input" />
          </div>
          <button onClick={add} disabled={busy} data-testid="client-add-btn" className="btn-primary rounded-lg px-4 py-2.5 text-sm mt-3 flex items-center gap-2 disabled:opacity-50">{busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add client</button>
        </div>

        {/* List */}
        {clients === null ? (
          <div className="p-12 flex justify-center"><Loader2 className="animate-spin text-brand" /></div>
        ) : clients.length === 0 ? (
          <p className="text-center text-slate-400 text-sm py-10">No clients yet.</p>
        ) : (
          <div className="space-y-3">
            {clients.map((c) => (
              <div key={c.id} data-testid="client-row" className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-4 flex-wrap">
                {c.logo_url ? (
                  <img src={c.logo_url} alt={c.name} className="w-9 h-9 rounded-lg object-contain border border-slate-100 shrink-0" onError={(e) => { e.target.style.display = "none"; }} />
                ) : (
                  <span className="w-9 h-9 rounded-lg shrink-0" style={{ background: c.brand_color || "#2563EB" }} />
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-800 flex items-center gap-2">{c.name} {c.demo && <span className="text-[9px] font-bold uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">demo</span>}</p>
                  <p className="text-xs text-slate-400">{c.website} · {c.lead_count} leads {c.email && `· ${c.email}`}</p>
                </div>
                <button onClick={() => copy(c.share_token)} data-testid="client-copy-link" className="btn-outline rounded-lg px-3 py-2 text-xs flex items-center gap-1.5"><Copy size={13} /> Copy link</button>
                <a href={linkFor(c.share_token)} target="_blank" rel="noopener noreferrer" data-testid="client-open-link" className="btn-outline rounded-lg px-3 py-2 text-xs flex items-center gap-1.5"><ExternalLink size={13} /> Open</a>
                {!c.demo && <button onClick={() => remove(c.id)} className="rounded-lg px-2.5 py-2 text-xs text-red-600 border border-slate-200 hover:border-red-300"><Trash2 size={13} /></button>}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
